import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle, FileText, Printer } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentConditions } from '../../../../../hooks/usePaymentConditions';
import { selectActiveCompany } from '../../../../../redux/slices/activeCompanySlice';
import { listBooksForSale } from '../../../../../services/BooksService';
import {
  BillingClient,
  addInvoiceLine,
  createInvoice,
  issueInvoice,
  listBillingClients,
  listInvoices,
  nextInvoiceNumber,
  updateInvoiceTerms
} from '../../../../../services/InvoicesService';
import { JournalLine } from '../../../../../services/JournalEntriesService';
import {
  formatEuro
} from '../masters/financeMasters';
import { printElement } from '../printElement';
import {
  AttachmentsField,
  DocumentLine,
  DocumentLinesEditor,
  FormSkeleton,
  InvoicePrefill,
  JournalPreview,
  LineProduct,
  SearchSelect,
  addDays,
  formatDate,
  linesSubtotal,
  linesVat,
  newLine,
  today
} from './FormParts';
import '../../shared-modules.css';
import '../masters/FinanceReports.css';
import './FinanceForms.css';

const SERIES = 'F-2026';
const REVENUE_ACCOUNT = { code: '700000', name: 'Ventas de mercaderías' };

interface IssuedInvoiceFormProps {
  privacyMode: boolean;
  prefill?: InvoicePrefill | null;
  onDone?: () => void;
}

export const IssuedInvoiceForm = ({ privacyMode, prefill, onDone }: IssuedInvoiceFormProps) => {
  const { notify } = useToast();
  const paperRef = useRef<HTMLDivElement>(null);

  const { conditions } = usePaymentConditions();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [products, setProducts] = useState<LineProduct[]>([]);
  const company = useSelector(selectActiveCompany);

  const [clientId, setClientId] = useState(prefill ? prefill.clientId : '');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [paymentTerms, setPaymentTerms] = useState('Neto 30 días');
  const [dueDate, setDueDate] = useState(addDays(today(), 30));
  const [orderRef, setOrderRef] = useState(prefill ? prefill.reference : '');
  const [lines, setLines] = useState<DocumentLine[]>(
    prefill && prefill.lines.length > 0 ? prefill.lines.map(l => ({ ...l, id: newLine().id })) : [newLine()]
  );
  const [notes, setNotes] = useState(prefill ? prefill.notes : '');
  const [attachments, setAttachments] = useState<File[]>([]);

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState<'' | 'draft' | 'issue'>('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clientList, books, invoices] = await Promise.all([
          listBillingClients(),
          listBooksForSale(),
          listInvoices()
        ]);
        if (cancelled) return;
        setClients(clientList);
        setProducts(books.map(b => ({ id: b.id, name: b.title, sku: b.isbn || b.sku, price: b.price, vat: 4, stock: b.stock })));
        setInvoiceNumber(nextInvoiceNumber(invoices, `${SERIES}-`));
        setLoadError('');
      } catch (err) {
        console.error('Error loading invoice form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los datos de facturación.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const conditionDays = (name: string) => conditions.find(c => c.name === name)?.days ?? 30;

  const handleIssueDateChange = (value: string) => {
    setIssueDate(value);
    if (value) setDueDate(addDays(value, conditionDays(paymentTerms)));
  };

  const handleTermsChange = (name: string) => {
    setPaymentTerms(name);
    setDueDate(addDays(issueDate, conditionDays(name)));
  };

  // Al elegir cliente se proponen sus condiciones de pago, si coinciden con alguna del maestro
  const handleClientChange = (id: string) => {
    setClientId(id);
    const client = clients.find(c => c.id === id);
    if (client && client.paymentTermsDays !== null) {
      const match = conditions.find(c => c.days === client.paymentTermsDays && c.isActive);
      if (match) handleTermsChange(match.name);
    }
  };

  const client = clients.find(c => c.id === clientId);
  const subtotal = linesSubtotal(lines);
  const vatTotal = linesVat(lines);
  const total = subtotal + vatTotal;

  const journalLines: JournalLine[] = [
    { account: client ? client.accountCode : '430000', accountName: 'Clientes', debit: total, credit: 0 },
    { account: REVENUE_ACCOUNT.code, accountName: REVENUE_ACCOUNT.name, debit: 0, credit: subtotal },
    { account: '477000', accountName: 'H.P. IVA Repercutido', debit: 0, credit: vatTotal }
  ];

  const validate = () => {
    if (!client) return 'Seleccione un cliente.';
    if (!invoiceNumber.trim()) return 'El número de factura es obligatorio.';
    if (lines.some(l => !l.description.trim())) return 'Todas las líneas necesitan una descripción.';
    // Existencias: se suman las unidades de cada artículo repartidas en varias líneas
    const wanted = new Map<string, number>();
    lines.forEach(l => { if (l.productId) wanted.set(l.productId, (wanted.get(l.productId) || 0) + l.quantity); });
    for (const [productId, units] of Array.from(wanted.entries())) {
      const p = products.find(pr => pr.id === productId);
      if (p && p.stock !== undefined && units > p.stock) {
        return `Sin stock suficiente de "${p.name}": se piden ${units} y hay ${Math.max(p.stock, 0)}.`;
      }
    }
    if (total <= 0) return 'El importe total de la factura debe ser mayor que cero.';
    if (dueDate && issueDate && dueDate < issueDate) return 'La fecha de vencimiento no puede ser anterior a la de emisión.';
    return '';
  };

  const saveInvoice = async (issue: boolean) => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(issue ? 'issue' : 'draft');

    let invoiceId = '';
    try {
      invoiceId = await createInvoice({
        clientId: client.id,
        number: invoiceNumber.trim(),
        subtotal,
        taxAmount: vatTotal,
        total
      });
      for (const l of lines) {
        await addInvoiceLine(invoiceId, {
          description: l.description.trim(),
          quantity: l.quantity,
          unitPrice: l.price,
          taxRate: l.vat
        });
      }
      await updateInvoiceTerms(invoiceId, { series: SERIES, dueDate });
    } catch (err) {
      console.error('Error saving invoice:', err);
      setFormError(`No se pudo guardar la factura: ${err instanceof Error ? err.message : 'error de comunicación con el servidor.'}`);
      setSaving('');
      return;
    }

    if (!issue) {
      notify('Borrador guardado', `Factura ${invoiceNumber} guardada como borrador.`, 'info');
      setSaving('');
      if (onDone) onDone();
      return;
    }

    try {
      await issueInvoice(invoiceId);
    } catch (err) {
      console.error('Error issuing invoice:', err);
      setFormError(`La factura ${invoiceNumber} se guardó como borrador, pero no se pudo emitir: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving('');
      return;
    }

    // El asiento lo genera el trigger trg_invoice_journal_entry de la base de datos al emitir
    notify('Factura emitida', `Factura ${invoiceNumber} emitida y contabilizada en el Libro Diario [${client.accountCode.slice(0, 3)} a 700 + 477].`, 'info');
    setSaving('');
    if (onDone) onDone();
  };

  const handlePreview = () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    if (paperRef.current) printElement(paperRef.current);
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const clientOptions = clients.map(c => ({ value: c.id, label: c.name, sub: c.cif ? `CIF: ${c.cif}` : undefined }));

  return (
    <form
      className="fin-doc"
      onSubmit={(e) => {
        e.preventDefault();
        saveInvoice(true);
      }}
      // Enter en un campo no emite la factura: solo el botón
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') e.preventDefault();
      }}
    >
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">VENTAS Y COBROS • FACTURACIÓN</span>
          <h3 className="fin-doc__title"><FileText size={14} /> Factura emitida</h3>
        </div>
        <span className="fin-doc__badge">SERIE {SERIES}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cliente (buscador) *</label>
          <SearchSelect
            options={clientOptions}
            value={clientId}
            onChange={handleClientChange}
            placeholder="Buscar cliente por nombre o CIF..."
            emptyText="No hay clientes"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Nº factura autonumérico</label>
          <input
            type="text"
            readOnly
            className="sidebar-form-input fin-doc__mono fin-doc__readonly"
            value={`${invoiceNumber} (Automático)`}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Referencia / Pedido</label>
          <input
            type="text"
            className="sidebar-form-input"
            value={orderRef}
            onChange={(e) => setOrderRef(e.target.value)}
            placeholder="Ej. PED-LIBR-2026-44"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha de emisión *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={issueDate}
            onChange={(e) => handleIssueDateChange(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha de vencimiento *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Condiciones de pago (maestro) *</label>
          <select className="sidebar-form-input" value={paymentTerms} onChange={(e) => handleTermsChange(e.target.value)}>
            {conditions.filter(c => c.isActive || c.name === paymentTerms).map(c => (
              <option key={c.id} value={c.name}>{c.name} ({c.days} días)</option>
            ))}
          </select>
        </div>
      </div>

      <DocumentLinesEditor lines={lines} onChange={setLines} products={products} privacyMode={privacyMode} checkStock />

      <div className="fin-doc__totals">
        <span>Base imponible: <strong>{formatEuro(subtotal, privacyMode)}</strong></span>
        <span>IVA repercutido: <strong>{formatEuro(vatTotal, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total factura: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <div className="fin-doc__two-cols">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Notas de la factura</label>
          <textarea
            rows={3}
            className="sidebar-form-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Condiciones de entrega, observaciones para el cliente..."
          />
        </div>
        <AttachmentsField files={attachments} onChange={setAttachments} />
      </div>

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled={Boolean(saving)} onClick={() => saveInvoice(false)}>
          {saving === 'draft' ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--outline" disabled={Boolean(saving)} onClick={handlePreview}>
          <Printer size={12} /> Previsualizar PDF
        </button>
        <button type="submit" className="fin-doc__btn fin-doc__btn--primary" disabled={Boolean(saving)}>
          {saving === 'issue' ? 'Emitiendo...' : 'Contabilizar y emitir'}
        </button>
      </div>

      {/* Documento que se imprime al previsualizar el PDF */}
      <div className="fin-doc__print-source">
        <div className="fin-report-paper" ref={paperRef}>
          <div className="fin-report-paper__header">
            <div>
              <h2 className="fin-report-paper__company">{company ? company.name : '—'}</h2>
              <p className="fin-report-paper__meta">
                {company ? `CIF: ${company.cif}${company.address ? ` • ${company.address}` : ''}` : ''}
              </p>
            </div>
            <div className="fin-report-paper__info">
              <p className="fin-report-paper__report-name">FACTURA {invoiceNumber}</p>
              <p>Fecha de emisión: {formatDate(issueDate)}</p>
              <p>Vencimiento: {formatDate(dueDate)}</p>
              {orderRef && <p>Referencia: {orderRef}</p>}
            </div>
          </div>

          <div className="fin-report-paper__section">
            <div className="fin-report-paper__summary">
              <div className="fin-report-paper__summary-row">
                <span className="strong">Cliente:</span>
                <span>{client ? client.name : '—'}</span>
              </div>
              <div className="fin-report-paper__summary-row">
                <span>CIF:</span>
                <span>{client ? client.cif : '—'}</span>
              </div>
              {client && client.address && (
                <div className="fin-report-paper__summary-row">
                  <span>Dirección:</span>
                  <span>{client.address}</span>
                </div>
              )}
            </div>

            <table className="fin-report-paper__table">
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th className="right">Cant.</th>
                  <th className="right">Precio</th>
                  <th className="right">IVA</th>
                  <th className="right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id}>
                    <td>{l.description}</td>
                    <td className="right">{l.quantity}</td>
                    <td className="right">{formatEuro(l.price, privacyMode)}</td>
                    <td className="right">{l.vat}%</td>
                    <td className="right">{formatEuro(l.quantity * l.price, privacyMode)}</td>
                  </tr>
                ))}
                <tr className="total">
                  <td colSpan={4}>Base imponible</td>
                  <td className="right">{formatEuro(subtotal, privacyMode)}</td>
                </tr>
                <tr className="total">
                  <td colSpan={4}>IVA</td>
                  <td className="right">{formatEuro(vatTotal, privacyMode)}</td>
                </tr>
                <tr className="result">
                  <td colSpan={4}>TOTAL FACTURA</td>
                  <td className="right">{formatEuro(total, privacyMode)}</td>
                </tr>
              </tbody>
            </table>

            <p className="fin-report-paper__disclaimer">
              Condiciones de pago: {paymentTerms}
              {notes ? ` • ${notes}` : ''}
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default IssuedInvoiceForm;
